import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { MainMenuSection } from '../components/MainMenu/types/MainMenu.types'
import Parse from 'parse'
import { getGuardResult, createRouteGuard, setupGuardCacheInvalidation } from '../utils/guardEvaluator'
import { useRbacStore } from './rbacStore'
import { isAdminRoute, normalizeRoutePath, logSecurityEvent, SecurityEventType } from '../utils/routeUtils'

// Admin route checking is now handled by the unified guard evaluator system

// Helper function to check if a route is an OAuth callback route
// These routes should bypass routing validation as they're handled by App component
const isOAuthCallbackRoute = (path: string): boolean => {
  return path === '/auth/google/callback' || 
         path.startsWith('/auth/') && path.includes('/callback')
}

// Helper function to check if a route is a public auth route (password reset, email verification, etc.)
// These routes should bypass routing validation as they're handled by App component
const isPublicAuthRoute = (path: string): boolean => {
  const p = normalizeRoutePath(path)
  return (
    p === '/reset-password' ||
    p.startsWith('/reset-password/') ||
    p === '/verify-email' ||
    p === '/accept-invitation' ||
    p === '/join-company' ||
    p === '/invitation-email-preview' ||
    p === '/meeting/join'
  )
}

// Route mapping for URL paths to MainMenu sections
const ROUTE_MAP: Record<string, MainMenuSection> = {
  // Main sections
  '/': 'publicLobby',
  '/dashboard': 'publicLobby',
  '/publicLobby': 'publicLobby',
  '/join': 'publicLobby',
  '/room': 'publicLobby',
  '/rooms': 'myRooms',
  '/myRooms': 'myRooms',
  '/meetings': 'meetings',
  '/chat': 'chat',
  '/profile': 'profile',
  // Profile sub-routes (open modal while staying in profile)
  '/profile/edit': 'profile',
  '/settings': 'settings',
  
  // Admin sections
  '/admin': 'adminMenu',
  '/adminMenu': 'adminMenu',
  '/admin/dashboard': 'adminDashboard',
  '/admin/environment': 'adminEnvironmentConfig',
  '/adminEnvironmentConfig': 'adminEnvironmentConfig',
  '/admin/entities': 'adminEntityCatalogue',
  '/adminEntityCatalogue': 'adminEntityCatalogue',
  '/admin/rooms': 'adminAllRooms',
  '/adminDefaultRooms': 'adminAllRooms',
  '/admin/billing': 'adminBillingSettings',
  '/adminBillingSettings': 'adminBillingSettings',
  '/admin/server-jobs': 'adminServerJobs',
  '/admin/maps': 'adminMaps',
  '/adminMaps': 'adminMaps',
  '/admin/transactional-emails': 'adminTransactionalEmails',
  '/adminTransactionalEmails': 'adminTransactionalEmails',
  '/admin/room-management': 'adminRoomManagement',
  '/admin/all-rooms': 'adminAllRooms',
  '/adminAllRooms': 'adminAllRooms',
  '/admin/users': 'adminUserManagement',
  '/admin/endpoint-testing': 'adminEndpointTesting',
  '/adminEndpointTesting': 'adminEndpointTesting',
  '/admin/product-updates': 'adminProductUpdates',
  '/adminProductUpdates': 'adminProductUpdates',
  '/admin/map-editor-plugins': 'adminMapEditorPlugins',
  '/admin/routing-test': 'routingTest',
  
  // Company Management sections
  '/company': 'companyManagement',
  '/companyManagement': 'companyManagement',
  '/company/overview': 'companyOverview',
  '/company/dashboard': 'companyDashboard',
  '/company/setup': 'companySetup',
  '/company/billing': 'companyBilling',
  '/company/users': 'companyUsers',
  '/company/settings': 'companySettings',
  '/company/wallet': 'companyWallet',
  
  // Company Management Dialogs
  '/company/dialogs/company': 'companyDialog',
  '/company/dialogs/invite-user': 'inviteUserDialog',
  '/company/dialogs/edit-user': 'editUserDialog',
  '/company/dialogs/users': 'usersDialog',
  '/company/dialogs/company-rooms': 'companyRoomsDialog',
}

// Reverse mapping for getting URL from section
const SECTION_TO_ROUTE: Record<MainMenuSection, string> = {
  // Main sections
  publicLobby: '/dashboard',
  myRooms: '/rooms',
  meetings: '/meetings',
  chat: '/chat',
  profile: '/profile',
  settings: '/settings',
  
  // Admin sections
  adminMenu: '/admin',
  adminDashboard: '/admin/dashboard',
  adminEnvironmentConfig: '/admin/environment',
  adminEntityCatalogue: '/admin/entities',
  adminAllRooms: '/admin/rooms',
  adminBillingSettings: '/admin/billing',
  adminServerJobs: '/admin/server-jobs',
  adminMaps: '/admin/maps',
  adminTransactionalEmails: '/admin/transactional-emails',
  adminRoomManagement: '/admin/room-management',
  adminUserManagement: '/admin/users',
  adminEndpointTesting: '/admin/endpoint-testing',
  adminProductUpdates: '/admin/product-updates',
  adminMapEditorPlugins: '/admin/map-editor-plugins',
  routingTest: '/admin/routing-test',
  
  // Company Management sections
  companyManagement: '/company/overview',
  companyOverview: '/company/overview',
  companyDashboard: '/company/dashboard',
  companySetup: '/company/setup',
  companyBilling: '/company/billing',
  companyUsers: '/company/users',
  companySettings: '/company/settings',
  companyWallet: '/company/wallet',
  
  // Company Management Dialogs
  companyDialog: '/company/dialogs/company',
  inviteUserDialog: '/company/dialogs/invite-user',
  editUserDialog: '/company/dialogs/edit-user',
  usersDialog: '/company/dialogs/users',
  companyRoomsDialog: '/company/dialogs/company-rooms',
}

interface RoutingState {
  // Current route state
  currentPath: string
  currentSection: MainMenuSection
  previousPath: string | null
  
  // Navigation history
  history: string[]
  historyIndex: number
  
  // Actions
  navigateTo: (path: string, replace?: boolean) => void
  navigateToSection: (section: MainMenuSection, replace?: boolean) => void
  goBack: () => void
  goForward: () => void
  canGoBack: () => boolean
  canGoForward: () => boolean
  
  // URL synchronization
  syncWithURL: () => void
  updateURL: (path: string, replace?: boolean) => void
  
  // Route validation
  isValidRoute: (path: string) => boolean
  getSectionFromPath: (path: string) => MainMenuSection | null

  // Route guard (now uses unified guard evaluator)
  routeGuard?: (path: string) => boolean
  setRouteGuard: (guard?: (path: string) => boolean) => void

  // Optional hard block predicate (e.g., subscription required):
  // When set, only allow paths that return true; otherwise force '/company'.
  hardBlock?: (path: string) => boolean
  setHardBlock: (block?: (path: string) => boolean) => void
}

export const useRoutingStore = create<RoutingState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    currentPath: '/',
    currentSection: 'publicLobby',
    previousPath: null,
    history: ['/'],
    historyIndex: 0,

    // Navigate to a specific path
    navigateTo: (path: string, replace: boolean = false) => {
      const state = get()
      
      console.log(`[Routing] navigateTo called with path: ${path}, replace: ${replace}`)
      
      // OAuth callback routes should bypass routing - they're handled by App component
      if (isOAuthCallbackRoute(path)) {
        console.log(`[Routing] OAuth callback route detected: ${path}, bypassing routing`)
        return
      }
      
      // Public auth routes (password reset) should bypass routing - they're handled by App component
      if (isPublicAuthRoute(path)) {
        console.log(`[Routing] Public auth route detected: ${path}, bypassing routing`)
        return
      }
      
      // Special redirect for /company to /company/overview
      if (path === '/company') {
        console.log(`[Routing] Redirecting /company to /company/overview`)
        const redirectPath = '/company/overview'
        const redirectSection = state.getSectionFromPath(redirectPath) || 'companyOverview'
        const newHistory = replace 
          ? state.history.slice(0, state.historyIndex) // Replace current entry
          : state.history.slice(0, state.historyIndex + 1) // Add new entry
        if (!replace) {
          newHistory.push(redirectPath)
        } else {
          newHistory[state.historyIndex] = redirectPath
        }
        set({
          previousPath: state.currentPath,
          currentPath: redirectPath,
          currentSection: redirectSection,
          history: newHistory,
          historyIndex: replace ? state.historyIndex : newHistory.length - 1,
        })
        state.updateURL(redirectPath, replace)
        return
      }
      
      // Hard block check (e.g., subscription required): only allow whitelisted paths
      if (state.hardBlock && !state.hardBlock(path)) {
        const forced = '/company/overview'
        console.warn(`[Routing] Hard block redirected '${path}' -> '${forced}'`)
        const forcedSection = state.getSectionFromPath(forced) || 'companyManagement'
        const newHistory = replace 
          ? state.history.slice(0, state.historyIndex)
          : state.history.slice(0, state.historyIndex + 1)
        if (!replace) {
          newHistory.push(forced)
        } else {
          newHistory[state.historyIndex] = forced
        }
        set({
          previousPath: state.currentPath,
          currentPath: forced,
          currentSection: forcedSection,
          history: newHistory,
          historyIndex: replace ? state.historyIndex : newHistory.length - 1,
        })
        state.updateURL(forced, replace)
        return
      }

      // Normalize path for consistent handling
      const normalizedPath = normalizeRoutePath(path)
      
      // Guard check using unified guard evaluator
      const guardResult = getGuardResult(normalizedPath)
      
      if (guardResult.loading) {
        // Security: For admin routes, block access while permissions are loading (fail closed)
        // For other routes, allow navigation but log warning
        if (isAdminRoute(normalizedPath)) {
          logSecurityEvent(SecurityEventType.BLOCKED_ADMIN_ROUTE, {
            path: normalizedPath,
            reason: 'Permissions loading - admin routes blocked',
          })
          
          const fallback = '/dashboard'
          const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
          const newHistory = replace 
            ? state.history.slice(0, state.historyIndex)
            : state.history.slice(0, state.historyIndex + 1)
          if (!replace) {
            newHistory.push(fallback)
          } else {
            newHistory[state.historyIndex] = fallback
          }
          set({
            previousPath: state.currentPath,
            currentPath: fallback,
            currentSection: fallbackSection,
            history: newHistory,
            historyIndex: replace ? state.historyIndex : newHistory.length - 1,
          })
          state.updateURL(fallback, replace)
          return
        }
        // For non-admin routes, allow navigation but log warning (only in development)
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Routing] Permissions still loading for route: ${normalizedPath}. Navigation allowed; route will be re-evaluated when permissions are ready.`)
        }
        // Continue with navigation - guard will re-evaluate when permissions are ready
      } else if (!guardResult.allowed) {
        // Guard blocked navigation
        console.warn(`[Routing] Guard blocked navigation to: ${path}`, guardResult.reason)
        
        const fallback = guardResult.redirectTo || '/dashboard'
        const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
        const newHistory = replace 
          ? state.history.slice(0, state.historyIndex)
          : state.history.slice(0, state.historyIndex + 1)
        if (!replace) {
          newHistory.push(fallback)
        } else {
          newHistory[state.historyIndex] = fallback
        }
        set({
          previousPath: state.currentPath,
          currentPath: fallback,
          currentSection: fallbackSection,
          history: newHistory,
          historyIndex: replace ? state.historyIndex : newHistory.length - 1,
        })
        state.updateURL(fallback, replace)
        return
      }


      // Validate route
      if (!state.isValidRoute(path)) {
        console.warn(`[Routing] Invalid route: ${path}, redirecting to safe fallback`)
        // Determine safe fallback based on user state
        const currentUser = (Parse as any).User?.current?.()
        const hasCompany = currentUser?.get?.('company') || currentUser?.get?.('companyId')
        const fallback = hasCompany ? '/dashboard' : '/company/overview'
        const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
        const newHistory = replace 
          ? state.history.slice(0, state.historyIndex)
          : state.history.slice(0, state.historyIndex + 1)
        if (!replace) {
          newHistory.push(fallback)
        } else {
          newHistory[state.historyIndex] = fallback
        }
        set({
          previousPath: state.currentPath,
          currentPath: fallback,
          currentSection: fallbackSection,
          history: newHistory,
          historyIndex: replace ? state.historyIndex : newHistory.length - 1,
        })
        state.updateURL(fallback, replace)
        return
      }

      const section = state.getSectionFromPath(path)
      if (!section) {
        console.warn(`[Routing] No section found for path: ${path}, redirecting to safe fallback`)
        // Determine safe fallback based on user state
        const currentUser = (Parse as any).User?.current?.()
        const hasCompany = currentUser?.get?.('company') || currentUser?.get?.('companyId')
        const fallback = hasCompany ? '/dashboard' : '/company/overview'
        const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
        const newHistory = replace 
          ? state.history.slice(0, state.historyIndex)
          : state.history.slice(0, state.historyIndex + 1)
        if (!replace) {
          newHistory.push(fallback)
        } else {
          newHistory[state.historyIndex] = fallback
        }
        set({
          previousPath: state.currentPath,
          currentPath: fallback,
          currentSection: fallbackSection,
          history: newHistory,
          historyIndex: replace ? state.historyIndex : newHistory.length - 1,
        })
        state.updateURL(fallback, replace)
        return
      }

      console.log(`[Routing] Navigating to section: ${section}`)

      // Update history
      const newHistory = replace 
        ? state.history.slice(0, state.historyIndex)
        : state.history.slice(0, state.historyIndex + 1)
      if (!replace) {
        newHistory.push(path)
      } else {
        newHistory[state.historyIndex] = path
      }
      
      set({
        previousPath: state.currentPath,
        currentPath: path,
        currentSection: section,
        history: newHistory,
        historyIndex: replace ? state.historyIndex : newHistory.length - 1,
      })

      // Update URL without triggering navigation
      state.updateURL(path, replace)
    },

    // Navigate to a specific section
    navigateToSection: (section: MainMenuSection, replace: boolean = false) => {
      const state = get()
      const path = SECTION_TO_ROUTE[section]
      console.log(`[Routing] navigateToSection called with section: ${section}, path: ${path}, replace: ${replace}`)
      state.navigateTo(path, replace)
    },

    // Go back in history
    goBack: () => {
      const state = get()
      if (state.canGoBack()) {
        const newIndex = state.historyIndex - 1
        const newPath = state.history[newIndex]
        const section = state.getSectionFromPath(newPath)
        
        if (section) {
          set({
            previousPath: state.currentPath,
            currentPath: newPath,
            currentSection: section,
            historyIndex: newIndex,
          })
          
          state.updateURL(newPath)
        }
      }
    },

    // Go forward in history
    goForward: () => {
      const state = get()
      if (state.canGoForward()) {
        const newIndex = state.historyIndex + 1
        const newPath = state.history[newIndex]
        const section = state.getSectionFromPath(newPath)
        
        if (section) {
          set({
            previousPath: state.currentPath,
            currentPath: newPath,
            currentSection: section,
            historyIndex: newIndex,
          })
          
          state.updateURL(newPath)
        }
      }
    },

    // Check if can go back
    canGoBack: () => {
      const state = get()
      return state.historyIndex > 0
    },

    // Check if can go forward
    canGoForward: () => {
      const state = get()
      return state.historyIndex < state.history.length - 1
    },

    // Sync with current URL (called on popstate events)
    syncWithURL: () => {
      const currentPath = window.location.pathname
      const state = get()
      
      console.log(`[Routing] syncWithURL called, currentPath: ${currentPath}, state.currentPath: ${state.currentPath}`)
      
      // OAuth callback routes should bypass routing - they're handled by App component
      if (isOAuthCallbackRoute(currentPath)) {
        console.log(`[Routing] OAuth callback route detected in syncWithURL: ${currentPath}, bypassing routing`)
        return
      }
      
      // Public auth routes (password reset) should bypass routing - they're handled by App component
      if (isPublicAuthRoute(currentPath)) {
        console.log(`[Routing] Public auth route detected in syncWithURL: ${currentPath}, bypassing routing`)
        return
      }
      
      if (currentPath !== state.currentPath) {
        // Special redirect for /company to /company/overview
        if (currentPath === '/company') {
          console.log(`[Routing] Direct URL access to /company, redirecting to /company/overview`)
          const redirectPath = '/company/overview'
          const redirectSection = state.getSectionFromPath(redirectPath) || 'companyOverview'
          // Use replaceState to avoid adding to history
          window.history.replaceState({}, '', redirectPath)
          set({
            currentPath: redirectPath,
            currentSection: redirectSection,
            previousPath: state.currentPath,
          })
          return
        }
        
        // Hard block for direct URL access
        if (state.hardBlock && !state.hardBlock(currentPath)) {
          const forced = '/company/overview'
          console.warn(`[Routing] Hard block prevented direct URL '${currentPath}', forcing '${forced}'`)
          const forcedSection = state.getSectionFromPath(forced) || 'companyManagement'
          // Use replaceState to avoid adding to history
          window.history.replaceState({}, '', forced)
          set({
            currentPath: forced,
            currentSection: forcedSection,
            previousPath: state.currentPath,
          })
          return
        }

        // Guard check for direct URL access (browser navigation) using unified guard evaluator
        try {
          const normalizedCurrentPath = normalizeRoutePath(currentPath)
          const currentUser = (Parse as any).User?.current?.()
          const isAuthenticated = !!currentUser?.getSessionToken?.()
          
          if (isAuthenticated) {
            // Apply guard using unified evaluator
            const guardResult = getGuardResult(normalizedCurrentPath)
            
            if (guardResult.loading) {
              // Security: For admin routes, block access while permissions are loading (fail closed)
              // For other routes, allow navigation but log warning
              if (isAdminRoute(normalizedCurrentPath)) {
                logSecurityEvent(SecurityEventType.BLOCKED_ADMIN_ROUTE, {
                  path: normalizedCurrentPath,
                  reason: 'Permissions loading - admin routes blocked (direct URL access)',
                  userId: currentUser?.id,
                })
                
                const fallback = '/dashboard'
                const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
                window.history.replaceState({}, '', fallback)
                set({
                  currentPath: fallback,
                  currentSection: fallbackSection,
                  previousPath: state.currentPath,
                })
                return
              }
              // For non-admin routes, allow navigation but log info (only in development)
              if (process.env.NODE_ENV === 'development') {
                console.log(`[Routing] Permissions still loading for direct URL access: ${normalizedCurrentPath}. Navigation allowed; route will be re-evaluated when permissions are ready.`)
              }
              // Continue with navigation - guard will re-evaluate when permissions are ready
            } else if (!guardResult.allowed) {
              // Guard blocked direct URL access
              console.warn(`[Routing] Guard blocked direct URL access to: ${currentPath}`, guardResult.reason)
              
              const fallback = guardResult.redirectTo || '/dashboard'
              const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
              // Use replaceState to avoid adding to history
              window.history.replaceState({}, '', fallback)
              set({
                currentPath: fallback,
                currentSection: fallbackSection,
                previousPath: state.currentPath,
              })
              return
            }
          } else {
            // User not authenticated - only allow public routes
            const publicRoutes = ['/', '/dashboard', '/publicLobby', '/join', '/room', '/rooms', '/myRooms', '/profile', '/settings']
            if (!publicRoutes.includes(currentPath)) {
              console.warn(`[Routing] Unauthenticated user accessing protected route: ${currentPath}, redirecting to dashboard`)
              const fallback = '/dashboard'
              const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
              window.history.replaceState({}, '', fallback)
              set({
                currentPath: fallback,
                currentSection: fallbackSection,
                previousPath: state.currentPath,
              })
              return
            }
          }
        } catch (error) {
          const normalizedCurrentPath = normalizeRoutePath(currentPath)
          const currentUser = (Parse as any).User?.current?.()
          
          // Security: For admin routes, fail closed (deny access on error)
          // For other routes, allow navigation to proceed (fail open)
          if (isAdminRoute(normalizedCurrentPath)) {
            logSecurityEvent(SecurityEventType.ROUTE_GUARD_ERROR, {
              path: normalizedCurrentPath,
              reason: `Error checking admin route guard: ${error instanceof Error ? error.message : String(error)}`,
              userId: currentUser?.id,
            })
            
            const fallback = '/dashboard'
            const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
            window.history.replaceState({}, '', fallback)
            set({
              currentPath: fallback,
              currentSection: fallbackSection,
              previousPath: state.currentPath,
            })
            return
          }
          // For non-admin routes, allow navigation to proceed (fail open)
          console.warn(`[Routing] Allowing navigation despite guard error (non-admin route): ${normalizedCurrentPath}`)
        }

        // Get section for the current path
        const section = state.getSectionFromPath(currentPath)
        
        // If route is invalid, redirect to safe fallback
        if (!section) {
          console.warn(`[Routing] Invalid route detected: ${currentPath}, redirecting to safe fallback`)
          // Determine safe fallback based on user state
          const currentUser = (Parse as any).User?.current?.()
          const hasCompany = currentUser?.get?.('company') || currentUser?.get?.('companyId')
          const fallback = hasCompany ? '/dashboard' : '/company/overview'
          const fallbackSection = state.getSectionFromPath(fallback) || 'publicLobby'
          
          // Use replaceState to avoid adding to history
          window.history.replaceState({}, '', fallback)
          set({
            currentPath: fallback,
            currentSection: fallbackSection,
            previousPath: state.currentPath,
          })
          return
        }

        // Valid route - update state
        console.log(`[Routing] Syncing to valid route: ${currentPath} -> ${section}`)
        set({
          currentPath,
          currentSection: section,
          previousPath: state.currentPath,
        })
      }
    },

    // Update URL without triggering navigation
    // Use replaceState parameter to control whether to add to history or replace
    updateURL: (path: string, replace: boolean = false) => {
      if (window.location.pathname !== path) {
        if (replace) {
          window.history.replaceState({}, '', path)
          console.log(`[Routing] URL replaced to: ${path}`)
        } else {
          window.history.pushState({}, '', path)
          console.log(`[Routing] URL updated to: ${path}`)
        }
      }
    },

    // Validate if route exists
    isValidRoute: (path: string) => {
      // OAuth callback routes are always valid (they bypass routing)
      if (isOAuthCallbackRoute(path)) {
        return true
      }
      // Public auth routes (password reset) are always valid (they bypass routing)
      if (isPublicAuthRoute(path)) {
        return true
      }
      return path in ROUTE_MAP
    },

    // Get section from path
    getSectionFromPath: (path: string) => {
      return ROUTE_MAP[path] || null
    },

    // Route guard setters
    // If guard is not provided, use the unified guard evaluator
    setRouteGuard: (guard?: (path: string) => boolean) => {
      const guardToUse = guard || createRouteGuard()
      set({ routeGuard: guardToUse })
      
      // Immediately check current route when guard is set
      const currentState = get()
      if (currentState.currentPath) {
        const guardResult = getGuardResult(currentState.currentPath)
        if (!guardResult.loading && !guardResult.allowed) {
          // Trigger navigation to apply guard (will redirect to fallback)
          currentState.navigateTo(currentState.currentPath, true)
        }
      }
    },

    // Hard block setter
    setHardBlock: (block?: (path: string) => boolean) => {
      const currentState = get()
      // Only update if the block function actually changed
      if (currentState.hardBlock !== block) {
        set({ hardBlock: block })
      }
    },
  }))
)

// initializeRouting runs from several places; one subscription is enough.
let stopWatchingRbac: (() => void) | null = null

// Initialize routing store with current URL
export const initializeRouting = () => {
  const store = useRoutingStore.getState()
  store.syncWithURL()
  
  // Set up unified guard evaluator cache invalidation
  setupGuardCacheInvalidation()

  // A company page opened while GET /api/rbac/me was still loading is let through (the guard
  // reports "loading"). Check it again once the answer is in, and again after any later change,
  // so a member who lacks the permission is sent back to the overview.
  if (!stopWatchingRbac) {
    stopWatchingRbac = useRbacStore.subscribe(
      (state) => state.version,
      () => {
        const routing = useRoutingStore.getState()
        const guardResult = getGuardResult(routing.currentPath)
        if (!guardResult.loading && !guardResult.allowed) {
          routing.navigateTo(routing.currentPath, true)
        }
      }
    )
  }
  
  // Initialize guard with unified evaluator if not already set
  if (!store.routeGuard) {
    store.setRouteGuard()
  }
}

// Set up browser navigation listeners
export const setupRoutingListeners = () => {
  // console.log('[Routing] Setting up routing listeners')
  
  // Handle browser back/forward buttons
  window.addEventListener('popstate', () => {
    console.log('[Routing] Popstate event detected')
    const store = useRoutingStore.getState()
    store.syncWithURL()
  })

  // Initialize with current URL
  // console.log('[Routing] Initializing routing with current URL:', window.location.pathname)
  initializeRouting()
}

// Export route constants for external use
export { ROUTE_MAP, SECTION_TO_ROUTE }